// Step-up verification client for the /api/cloud/auth/verify routes. A
// verification grant is an opaque, single-use token with a short (5-minute)
// TTL, bound to the signed-in user, one action and, for a team deletion, that
// team; sensitive endpoints (API-key creation, account export and deletion,
// team deletion) demand one so a stolen session JWT alone cannot mint
// long-lived credentials or erase data. Both proof paths — email OTP and
// passkey assertion — ride the AUTHED fetch helpers: the caller already has a
// session and only needs to re-prove presence. Unlike loginWithPasskey,
// verifyWithPasskey never touches pb.authStore — a step-up assertion earns a
// grant, it does not mint or replace the session.

import { sendJSON } from "./http.js";
import {
  parseRequestOptions,
  credentialToJSON,
  type RequestOptionsWire,
} from "./webauthn.js";

const BASE = "/api/cloud/auth/verify";

/**
 * StepUpAction names what a grant will authorize. The ids must match the Go
 * vocabulary (internal/stepup) exactly — the backend rejects anything else.
 * `delete_team` needs the team id as target; every other action takes none.
 * The server refuses a missing or misplaced target with `validation_failed`
 * before it looks at the code or the passkey, so neither is spent.
 */
export type StepUpAction = "create_api_key" | "export_account" | "delete_account" | "delete_team";

/** requestStepUpOtp emails the signed-in user a one-time code and returns the
 * id to confirm it with. */
export function requestStepUpOtp(): Promise<{ otpId: string }> {
  return sendJSON<{ otpId: string }>(`${BASE}/otp/request`, "POST", {});
}

/**
 * confirmStepUpOtp redeems an emailed code for a verification grant. The
 * server answers a uniform `otp_invalid` for wrong, expired, and foreign
 * codes alike, so callers have exactly one failure path.
 */
export async function confirmStepUpOtp(
  otpId: string,
  code: string,
  action: StepUpAction,
  target?: string,
): Promise<string> {
  const res = await sendJSON<{ verification: string }>(`${BASE}/otp/confirm`, "POST", {
    otpId,
    code,
    action,
    target,
  });
  return res.verification;
}

/**
 * verifyWithPasskey runs the step-up assertion ceremony for the signed-in
 * user: begin → navigator.credentials.get (the browser prompts for
 * fingerprint / face / PIN) → finish, and returns the verification grant.
 * Rejects with a DOMException (NotAllowedError/AbortError) when the user
 * dismisses the browser prompt — callers treat that as a cancel via
 * passkeyFailureMessage, not an error.
 */
export async function verifyWithPasskey(action: StepUpAction, target?: string): Promise<string> {
  const begin = await sendJSON<{ sessionId: string; options: RequestOptionsWire }>(
    `${BASE}/passkey/begin`,
    "POST",
    {},
  );
  const cred = await navigator.credentials.get({ publicKey: parseRequestOptions(begin.options) });
  if (!cred) throw { code: "invalid_credential", message: "" };
  const res = await sendJSON<{ verification: string }>(`${BASE}/passkey/finish`, "POST", {
    sessionId: begin.sessionId,
    credential: credentialToJSON(cred as PublicKeyCredential),
    action,
    target,
  });
  return res.verification;
}
